import { LogLevel } from "amazon-chime-sdk-js";

export const SDK_LOG_LEVELS = {
  debug: LogLevel.DEBUG,
  info: LogLevel.INFO,
  warn: LogLevel.WARN,
  error: LogLevel.ERROR,
  off: LogLevel.OFF,
} as const;

export const VIDEO_QUALITY = {
  width: 1280,
  height: 720,
  frameRate: 15,
  maxBandwidthKbps: 1500,
} as const;

/** Relaxation tier used when the HD attempt throws OverconstrainedError.
 *  We retry the same camera at 640x480x15 before falling through to
 *  startVideoInputDevice('default'). Some cameras (older webcams,
 *  virtual cameras, mobile front-cams) advertise capabilities that don't
 *  hold up under the HD pipeline; this tier is the conservative SD
 *  fallback that nearly every device supports. Kept as a sibling to
 *  VIDEO_QUALITY so the two tiers are discoverable together. */
export const VIDEO_QUALITY_RELAXED = {
  width: 640,
  height: 480,
  frameRate: 15,
} as const;

// --- Grace / timing constants (shared across hooks) ---

/** Grace period before accepting remote video is gone (good connection).
 *  Must be long enough for the SDK's connectionDidBecomePoor callback to
 *  fire (typically 5-10s) so the grace timer can reschedule on degraded
 *  quality instead of committing to disable during a network blip.
 *  Set to 12s (2s buffer over RECOVERY_COOLDOWN_MS) to ensure the recovery
 *  cooldown always completes before this timer fires. */
export const GRACE_GOOD_MS = 12_000;

/** Grace period before accepting remote video is gone (degraded connection) */
export const GRACE_POOR_MS = 30_000;

/** Grace period before removing attendee from grid after roster drop.
 *  3s covers WiFi handoffs and cellular switches (typically 500ms-2s).
 *  Longer outages are handled by the Chime SDK's reconnect guard, which
 *  freezes all tiles and buffers events independently of this timer. */
/** Grace period before removing attendee from grid after roster drop.
 *  3s covers WiFi handoffs and cellular switches (typically 500ms-2s).
 *  Longer outages are handled by the Chime SDK's reconnect guard, which
 *  freezes all tiles and buffers events independently of this timer. */
export const ROSTER_GRACE_MS = 3_000;

/** Cooldown before transitioning back to 'good' after recovery */
export const RECOVERY_COOLDOWN_MS = 10_000;

/** Safety timeout to clear reconnect guard if audioVideoDidStart never fires */
export const RECONNECT_GUARD_TIMEOUT_MS = 20_000;

/** Timeout for mass-drop detection self-heal */
export const MASS_DROP_VALIDATION_MS = 15_000;

/** Heartbeat interval for device state data channel */
export const DEVICE_STATE_HEARTBEAT_MS = 15_000;

/** Staleness threshold for device intent data messages */
export const INTENT_STALENESS_MS = 30_000;

/** Interval for checking if a video element has decodable frames */
export const VIDEO_HEALTH_CHECK_INTERVAL_MS = 3_000;

/** Interval for capturing video frames to canvas fallback */
export const FRAME_CAPTURE_INTERVAL_MS = 3_000;

/** Max canvas capture dimensions — reduces memory from ~3.7MB to ~920KB per tile */
export const MAX_CAPTURE_WIDTH = 640;
export const MAX_CAPTURE_HEIGHT = 360;

